// Google Analytics 4 (W.4.58) — idle-deferred gtag loader.
//
// UX contract: gtag.js (~50KB gzipped) must never compete with app bootstrap.
// The loader waits for the window `load` event, then yields to
// requestIdleCallback before injecting the script tag, so FCP/LCP/TTI and the
// React mount are untouched — the only cost is a network fetch after the page
// is already interactive. The gtag command queue (`dataLayer`) is primed at
// inject time and replayed by the script when it arrives; `config`, which sends
// the initial page_view, follows once the script has run (see inject).
//
// SPA route changes need no router integration: the GA4 web stream
// (G-5W72M4QEVS, stream 13033833162) has Enhanced Measurement on, which
// tracks page_view on browser history events.
//
// Gating is stricter than PostHog's PROD-only check: an explicit hostname
// allowlist keeps staging Workers, *.okrapdf.dev hosted links, and local
// preview builds out of the property.

import { Effect } from 'effect';
import {
  brandByHostnameEffect,
  isAppHostnameEffect,
  isMarketingHostnameEffect,
} from './brand';

export const GA_MEASUREMENT_ID = 'G-5W72M4QEVS';

export const isGaHostEffect = Effect.fn('web.ga.isHost')(function* (hostname: string) {
  const brand = yield* brandByHostnameEffect(hostname);
  if (!brand?.gaMeasurementId) return false;
  return (yield* isAppHostnameEffect(hostname)) || (yield* isMarketingHostnameEffect(hostname));
});

export function isGaHost(hostname: string): boolean {
  return Effect.runSync(isGaHostEffect(hostname));
}

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

let injected = false;
let pinnedPageTitle: string | undefined;

/**
 * gtag sends document.title as `dt` on every hit, Enhanced Measurement page
 * views included, and the tab names the file open in the Studio (W.4.132). Pin
 * a title before a file name reaches the tab; `undefined` goes back to
 * document.title once it has left.
 */
export function setGaPageTitle(title: string | undefined) {
  pinnedPageTitle = title;
  window.gtag?.('set', 'page_title', title);
}

function inject() {
  if (injected) return;
  injected = true;

  window.dataLayer = window.dataLayer || [];
  // gtag.js requires `arguments` objects on the queue — pushing plain arrays
  // silently registers nothing.
  window.gtag = function gtag() {
    window.dataLayer!.push(arguments);
  };
  window.gtag('js', new Date());
  // A file opened before this idle-deferred load: the first page_view is next.
  if (pinnedPageTitle !== undefined) window.gtag('set', 'page_title', pinnedPageTitle);

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  // gtag reads document.title for the first page_view as it replays `config`,
  // and a pin queued behind `config` is too late for that hit. Holding `config`
  // until the script has run keeps every pin made on its way in front of it.
  script.onload = () => window.gtag?.('config', GA_MEASUREMENT_ID);
  document.head.appendChild(script);
}

function whenIdle(fn: () => void) {
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(() => fn(), { timeout: 5000 });
  } else {
    // Safari has no requestIdleCallback.
    setTimeout(fn, 1500);
  }
}

export const initGoogleAnalyticsEffect = Effect.fn('web.ga.init')(function* () {
  if (typeof window === 'undefined') return;
  if (!import.meta.env.PROD) return;
  if (!(yield* isGaHostEffect(window.location.hostname))) return;
  if (injected) return;

  if (document.readyState === 'complete') {
    whenIdle(inject);
  } else {
    window.addEventListener('load', () => whenIdle(inject), { once: true });
  }
});

export function initGoogleAnalytics() {
  Effect.runSync(initGoogleAnalyticsEffect());
}
